import { useEffect, useState } from "react";
import { SingleDatePicker, AnchorDirectionShape } from "react-dates";
import { FC } from "react";
import moment from "moment";
import useWindowSize from "hooks/useWindowResize";
import useNcId from "hooks/useNcId";
import { useSelector } from "react-redux";

export interface PlaceDateSingleInputProps {
  defaultValue: moment.Moment | null;
  onChange?: (date: moment.Moment | null) => void;
  defaultFocus?: boolean;
  fieldClassName?: string;
  onFocusChange?: (focused: boolean) => void;
  className?: string;
  anchorDirection?: AnchorDirectionShape;
  notAvailableDates?: { from: string; to: string }[];
  bankHolidays?: Date[];
  setSelectedDate?: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const PlaceDateSingleInput: FC<PlaceDateSingleInputProps> = ({
  defaultValue,
  onChange,
  defaultFocus = false,
  onFocusChange,
  anchorDirection,
  className = "",
  fieldClassName = "[ nc-hero-field-padding ]",
  notAvailableDates = [],
  bankHolidays = [],
  setSelectedDate,
}) => {
  const [focusedInput, setFocusedInput] = useState(defaultFocus);
  const [startDate, setStartDate] = useState(defaultValue);
  // const [disabledDates, setDisabledDates] = useState(null);
  const startDateId = useNcId();
  const TravelDates = useSelector((state: any) => state.search.dates);
  const startDateQuery = TravelDates.startDate;
  const endDateQuery = TravelDates.endDate;

  const windowSize = useWindowSize();

  useEffect(() => {
    setStartDate(defaultValue);
    setSelectedDate && setSelectedDate(defaultValue?.format("l"));
  }, []);

  useEffect(() => {
    setFocusedInput(defaultFocus);
  }, [defaultFocus]);

  const handleDateFocusChange = (arg: { focused: boolean }) => {
    setFocusedInput(arg.focused);
    onFocusChange && onFocusChange(arg.focused);
  };

  const renderInputCheckInDate = () => {
    const focused = focusedInput;
    return (
      <div
        className={`flex-1 flex relative ${fieldClassName} items-center space-x-3 cursor-pointer ${
          focused ? "nc-hero-field-focused" : ""
        }`}
      >
        <div className="text-neutral-300 dark:text-neutral-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="nc-icon-field"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </div>
        <div className="flex-grow">
          <span className="block xl:text-lg font-semibold">
            {startDate ? startDate.format("DD MMM") : "Date"}
          </span>
          <span className="block mt-1 text-sm text-neutral-400 leading-none font-light">
            {startDate ? "Date" : `Add date`}
          </span>
        </div>
      </div>
    );
  };

  const handleClick = () => {
    // Set the focused input to `true` when the container is clicked
    setFocusedInput(true);

    // Find the first available date between startDate and endDate
    const currentDate = moment(startDateQuery);
    const endDate = moment(endDateQuery);
    let newStartDate = null;

    while (currentDate.isSameOrBefore(endDate, "day")) {
      const isDateDisabled = notAvailableDates.some(({ from, to }) => {
        const startDate = moment(from, "DD/MM");
        const endDate = moment(to, "DD/MM");

        if (startDate.isSame(endDate, "day")) {
          return currentDate.isSame(startDate, "day");
        } else {
          return currentDate.isBetween(startDate, endDate, "day", "[]");
        }
      });
      const isBankHoliday = bankHolidays.some((date) =>
        currentDate.isSame(date, "day")
      );

      if (!isDateDisabled && !isBankHoliday) {
        newStartDate = currentDate.clone();
        break;
      }

      currentDate.add(1, "day");
    }

    // Set the new startDate if found
    if (newStartDate) {
      setStartDate(newStartDate);
      onChange && onChange(newStartDate);
    }
  };

  const isOutsideRange = (date: moment.Moment | null): boolean => {
    if (!date) return false;

    const isDateBeforeStart = date.isBefore(startDateQuery, "day");
    const isDateAfterEnd = date.isAfter(endDateQuery, "day");
    const isBankHoliday = bankHolidays.some((bankDate: Date) =>
      date.isSame(moment(bankDate), "day")
    );

    const isDateDisabled = notAvailableDates.some(({ from, to }) => {
      const startDate = moment(from, "DD/MM");
      const endDate = moment(to, "DD/MM");

      const isRangeInsideNotAvailableDates =
        startDate.isSameOrBefore(date, "day") &&
        endDate.isSameOrAfter(date, "day");

      return isRangeInsideNotAvailableDates;
    });

    return (
      isDateDisabled || isDateBeforeStart || isDateAfterEnd || isBankHoliday
    );
  };

  useEffect(() => {}, []);

  return (
    <div
      className={`PlaceDateSingleInput relative flex ${className} ${
        !!focusedInput ? "nc-date-focusedInput" : "nc-date-not-focusedInput"
      }`}
      onClick={handleClick}
    >
      <div className="absolute inset-0 flex">
        <SingleDatePicker
          date={startDate}
          onDateChange={(date) => {
            setStartDate(date);
            onChange && onChange(date);
          }}
          id={startDateId}
          focused={focusedInput}
          daySize={windowSize.width > 1279 ? 56 : 44}
          orientation={"horizontal"}
          onFocusChange={handleDateFocusChange}
          noBorder
          hideKeyboardShortcutsPanel
          numberOfMonths={1}
          anchorDirection={anchorDirection}
          showClearDate
          reopenPickerOnClearDate
          isOutsideRange={isOutsideRange}
        />
      </div>

      {renderInputCheckInDate()}
    </div>
  );
};

export default PlaceDateSingleInput;
